import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../translations/translations';

export const HeroSection = () => {
  const { language } = useLanguage();

  const features = [
    { icon: 'Scale', label: t(language, 'hero.lawyerLed'), delay: '0.3s' },
    { icon: 'FileCheck', label: t(language, 'hero.notaryBacked'), delay: '0.5s' },
    { icon: 'Globe', label: t(language, 'hero.fullyOnline'), delay: '0.7s' },
    { icon: 'Target', label: t(language, 'hero.tailoredFounders'), delay: '0.9s' }
  ];

  return (
    <section className="relative bg-[#fefdf4] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium leading-[1.05] text-gray-900 mb-4">
                {t(language, 'hero.subtitle')}
              </h1>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed">
              {t(language, 'hero.description')}
            </p>

            <p className="text-lg text-gray-600 leading-relaxed">
              {t(language, 'hero.description2')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/start">
                <Button size="lg" className="shine-cta w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-8">
                  {t(language, 'nav.startCta')}
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-blue-900 to-gray-900 rounded-2xl shadow-2xl p-12 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full opacity-10 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full opacity-5 blur-2xl"></div>

              <div className="relative z-10 space-y-8">
                <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  <h3 className="text-3xl font-medium text-white mb-2">
                    {t(language, 'hero.animatedTitle')}
                  </h3>
                  <h3 className="text-3xl font-medium text-blue-300">
                    {t(language, 'hero.animatedSubtitle')}
                  </h3>
                </div>

                <div className="space-y-4 pt-4">
                  {features.map((feature) => {
                    const Icon = LucideIcons[feature.icon];
                    return (
                      <div
                        key={feature.icon}
                        className="flex items-center space-x-4 animate-fade-in-left"
                        style={{ animationDelay: feature.delay }}
                      >
                        <div className="flex-shrink-0 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                          <Icon className="w-6 h-6 text-blue-300" />
                        </div>
                        <span className="text-lg font-medium text-white">
                          {feature.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="pt-6 animate-fade-in" style={{ animationDelay: '1.1s' }}>
                  <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-blue-300 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="absolute -z-10 -top-4 -right-4 w-72 h-72 bg-blue-900 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute -z-10 -bottom-4 -left-4 w-64 h-64 bg-gray-800 rounded-full opacity-20 blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
